import { useCartState } from "@made-people/grease-cart";
import { Product } from "@made-people/grease-product";
import { useEffect, useState } from "react";
import { useFrontendContext } from "./use-frontend-context";
import { BffClient } from "@made-people/gears-bff-client";
import {
  useGearsServiceUrl,
  useGearsConfigurationVariable,
} from "@made-people/gears-configuration";
import { frontendProductToGreaseProduct } from "../services/bff/mappers/frontend-product-to-grease-product";
import { useAllocatedWarehouseIds } from "../services/centra/allocation-rule/use-allocated-warehouse-ids";
import { FrontendProduct } from "@made-people/nestjs-bff-service";

export const useMinicartRecommendations = (relationType?: string) => {
  const { cart } = useCartState();
  const frontendContext = useFrontendContext();
  const allocatedWarehouseIds = useAllocatedWarehouseIds();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cart?.items?.length || !bffUrl) {
      setRecommendations([]);
      return;
    }

    const lastItem = cart.items[cart.items.length - 1];
    const productId = lastItem.product.base.productId;

    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        const bffClient = new BffClient({
          baseUrl: bffUrl,
          tenantId,
        });

        const response = await bffClient.fetchFrontendProducts({
          frontendContext,
          payload: {
            products: [productId],
            relatedProducts: true,
            limit: 1,
          },
        });

        if (response?.frontendProducts?.[0]?.relatedProducts) {
          const relatedProducts = response.frontendProducts[0].relatedProducts;

          const filteredProducts = relationType
            ? relatedProducts.filter(
                (rp: FrontendProduct) => rp.relation === relationType,
              )
            : relatedProducts;

          const mappedProducts = filteredProducts.map(
            (relatedProduct: FrontendProduct) =>
              frontendProductToGreaseProduct(
                relatedProduct,
                frontendContext,
                allocatedWarehouseIds,
              ),
          );

          setRecommendations(mappedProducts);
        } else {
          setRecommendations([]);
        }
      } catch (error) {
        console.error("Failed to fetch minicart recommendations:", error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [cart?.items, frontendContext, bffUrl, tenantId, relationType]);

  return { recommendations, loading };
};
